import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import logoBlanco from '../../assets/logo_blanco.png';
import logoVerde from '../../assets/logo_verde.png';

const MOBILE_MENU_ID = 'menu-movil';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, toggleLang, t } = useLang();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Con el menú de celular abierto: Escape lo cierra y el fondo no se desplaza.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (e) => e.key === 'Escape' && setMobileOpen(false);
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const closeMenu = () => setMobileOpen(false);

  const navLinks = [
    { label: t.nav.home, to: 'inicio' },
    { label: t.nav.services, to: 'servicios' },
    { label: t.nav.about, to: 'quienes-somos' },
    { label: t.nav.projects, to: 'proyectos' },
    { label: t.nav.gallery, to: 'galeria' },
    { label: t.nav.contact, to: 'contacto' },
  ];

  return (
    <>
      <nav
        aria-label={t.nav.mainNav}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          boxShadow: scrolled ? '0 1px 40px rgba(27,67,50,0.1)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#inicio" onClick={closeMenu} className="rounded-sm">
            <img
              src={scrolled ? logoVerde : logoBlanco}
              alt="SAVIA Sustainability Advisors"
              width="480"
              height="231"
              className="h-11 md:h-14 w-auto transition-all duration-300"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.to}
                href={`#${link.to}`}
                className={`text-sm font-medium transition-colors duration-300 hover:text-accent ${
                  scrolled ? 'text-dark' : 'text-white/90'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={toggleLang}
              className={`text-sm font-medium px-3 py-1.5 rounded-full border transition-all duration-300 ${
                scrolled
                  ? 'border-gray-300 text-dark hover:border-accent'
                  : 'border-white/30 text-white hover:border-white/60'
              }`}
              aria-label={t.nav.toggleLanguage}
            >
              {lang === 'es' ? 'EN' : 'ES'}
            </button>
            <a
              href="#contacto"
              className="px-6 py-2.5 bg-primary text-white text-sm font-semibold rounded-full hover:bg-secondary transition-all duration-300 flex items-center gap-2"
            >
              {t.nav.cta}
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen((open) => !open)}
            className="lg:hidden z-50 p-2 -mr-2"
            aria-label={mobileOpen ? t.nav.closeMenu : t.nav.openMenu}
            aria-expanded={mobileOpen}
            aria-controls={MOBILE_MENU_ID}
          >
            {mobileOpen ? (
              <X size={24} className="text-dark" aria-hidden="true" />
            ) : (
              <Menu size={24} className={scrolled ? 'text-dark' : 'text-white'} aria-hidden="true" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id={MOBILE_MENU_ID}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white flex flex-col justify-center items-center gap-8 overflow-y-auto py-24"
          >
            {navLinks.map((link) => (
              <a
                key={link.to}
                href={`#${link.to}`}
                onClick={closeMenu}
                className="text-2xl font-display font-bold text-primary hover:text-accent transition-colors"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={toggleLang}
              className="text-sm font-medium px-4 py-2 rounded-full border border-gray-300 text-dark"
              aria-label={t.nav.toggleLanguage}
            >
              {lang === 'es' ? 'English' : 'Español'}
            </button>
            <a
              href="#contacto"
              onClick={closeMenu}
              className="px-8 py-3 bg-primary text-white font-semibold rounded-full hover:bg-secondary transition-all"
            >
              {t.nav.cta}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
